import { supabase } from '../lib/supabase';
import { safeStorageName } from '../utils/safeStorageName';

/**
 * Pieces jointes, generiques : les fichiers restent dans le bucket
 * contracts-documents, la LISTE vit dans une table (plus aucun listage Storage
 * pour compter ou afficher).
 *
 *   'extra'     → extra_documents     (extra_id)     · extra/{id}/
 *   'directive' → directive_documents (directive_id) · directives/{id}/
 *
 * file_path = cle Storage (prefixe horodate), ne bouge jamais ; file_name = nom
 * d'origine, affiche (sans le prefixe). Meme modele que les directives
 * (projectDirectivesService), dont ce service reprend le comportement.
 */

const BUCKET = 'contracts-documents';

const CONFIG = {
  extra: { table: 'extra_documents', fk: 'extra_id', prefix: (id) => `extra/${id}` },
  directive: { table: 'directive_documents', fk: 'directive_id', prefix: (id) => `directives/${id}` },
};

const configOf = (entityType) => {
  const cfg = CONFIG?.[entityType];
  if (!cfg) throw new Error(`entityDocumentsService: type inconnu "${entityType}"`);
  return cfg;
};

const selectOf = (cfg) => `id, ${cfg?.fk}, project_id, file_name, file_path, mime_type, size_bytes, created_at, created_by`;

const currentUserId = async () => {
  try {
    const { data } = await supabase?.auth?.getUser();
    return data?.user?.id || null;
  } catch {
    return null;
  }
};

/**
 * Compteurs (et noms) de toutes les entites d'un projet, en UNE requete :
 * { [entityId]: { count, names: [...] } }. null si la lecture echoue
 * (« inconnu », jamais « 0 »).
 */
export const listCounts = async (entityType, projectId) => {
  if (!projectId) return {};
  const cfg = configOf(entityType);
  const { data, error } = await supabase
    ?.from(cfg?.table)
    ?.select(`${cfg?.fk}, file_name, created_at`)
    ?.eq('project_id', projectId)
    ?.order('created_at', { ascending: false });
  if (error) {
    console.error(`[documents:${entityType}] listCounts:`, error?.message);
    return null;
  }
  return (data || [])?.reduce((acc, row) => {
    const key = row?.[cfg?.fk];
    const entry = (acc[key] ||= { count: 0, names: [] });
    entry.count += 1;
    entry.names?.push(row?.file_name);
    return acc;
  }, {});
};

/** Pieces d'une entite, plus recentes d'abord. */
export const list = async (entityType, entityId) => {
  if (!entityId) return [];
  const cfg = configOf(entityType);
  const { data, error } = await supabase
    ?.from(cfg?.table)
    ?.select(selectOf(cfg))
    ?.eq(cfg?.fk, entityId)
    ?.order('created_at', { ascending: false });
  if (error) {
    console.error(`[documents:${entityType}] list:`, error?.message);
    throw error;
  }
  return data || [];
};

/**
 * Televerse dans {prefixe}/{Date.now()}_{nom}, puis enregistre la ligne. Si
 * l'enregistrement echoue, l'objet Storage est retire : jamais d'orphelin.
 */
export const upload = async (entityType, entityId, file, projectId) => {
  if (!entityId || !file) return null;
  const cfg = configOf(entityType);
  const path = `${cfg?.prefix(entityId)}/${Date.now()}_${safeStorageName(file?.name)}`;
  const { error } = await supabase?.storage?.from(BUCKET)?.upload(path, file, { upsert: false, cacheControl: '3600' });
  if (error) {
    console.error(`[documents:${entityType}] upload:`, error?.message);
    throw error;
  }
  const { data: row, error: insertError } = await supabase
    ?.from(cfg?.table)
    ?.insert({
      [cfg?.fk]: entityId,
      project_id: projectId,
      file_path: path,
      file_name: file?.name || safeStorageName(file?.name),
      mime_type: file?.type || null,
      size_bytes: file?.size ?? null,
      created_by: await currentUserId(),
    })
    ?.select(selectOf(cfg))
    ?.single();
  if (insertError) {
    console.error(`[documents:${entityType}] upload (enregistrement):`, insertError?.message);
    await supabase?.storage?.from(BUCKET)?.remove([path]);
    throw insertError;
  }
  return row;
};

/**
 * Supprime une piece : la ligne d'abord (elle disparait de la liste), puis
 * l'objet Storage. Un objet restant n'est plus visible ; une ligne sans objet
 * afficherait un lien mort.
 */
export const remove = async (entityType, doc) => {
  if (!doc?.id) return;
  const cfg = configOf(entityType);
  const { error } = await supabase?.from(cfg?.table)?.delete()?.eq('id', doc?.id);
  if (error) {
    console.error(`[documents:${entityType}] remove:`, error?.message);
    throw error;
  }
  if (doc?.file_path) {
    const { error: storageError } = await supabase?.storage?.from(BUCKET)?.remove([doc?.file_path]);
    if (storageError) console.error(`[documents:${entityType}] remove (Storage):`, storageError?.message);
  }
};

// Extension d'un nom, point compris (« .pdf »), ou '' s'il n'y en a pas.
const extensionOf = (name) => {
  const match = String(name || '')?.match(/\.[A-Za-z0-9]{1,8}$/);
  return match ? match?.[0] : '';
};

/** Renomme : seul file_name change ; l'extension est remise si retiree. */
export const rename = async (entityType, id, newName, currentName) => {
  const cfg = configOf(entityType);
  let name = String(newName || '')?.trim();
  if (!id || !name) throw new Error('EMPTY_NAME');
  const ext = extensionOf(currentName);
  if (ext && !name?.toLowerCase()?.endsWith(ext?.toLowerCase())) name = `${name}${ext}`;
  const { data, error } = await supabase
    ?.from(cfg?.table)
    ?.update({ file_name: name })
    ?.eq('id', id)
    ?.select(selectOf(cfg))
    ?.single();
  if (error) {
    console.error(`[documents:${entityType}] rename:`, error?.message);
    throw error;
  }
  return data;
};

/** URL signee ; `download` force le telechargement sous le nom affiche. */
export const getSignedUrl = async (doc, download = false) => {
  if (!doc?.file_path) return null;
  const { data, error } = await supabase?.storage
    ?.from(BUCKET)
    ?.createSignedUrl(doc?.file_path, 60 * 60, download ? { download: doc?.file_name || true } : undefined);
  if (error) throw error;
  return data?.signedUrl || null;
};

const entityDocumentsService = { listCounts, list, upload, remove, rename, getSignedUrl };
export default entityDocumentsService;
